import { useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { createTrackingProps } from 'shared-utils/analytics';
import gradientOfficeIcon from 'design-system/src/assets/icons/gradientOffice.png';
import prescriptionEmptyState from '../../assets/prescriptionEmptyState.png';
import tasksEmptyState from '../../assets/tasksEmptyState.png';
import notificationsEmptyState from '../../assets/notificationsEmptyState.png';
import expiringPAsEmptyState from '../../assets/expiringPAsEmptyState.png';
import gradientProvider from 'design-system/src/assets/icons/gradientProvider.png';
import paReviewsEmptyState from '../../assets/paReviewsEmptyState.png';

// TODO(WIT-4362): fix
// eslint-disable-next-line react-refresh/only-export-components
export enum TableNullStateType {
  // used by all tables if there are no results and filters are applied
  NO_RESULTS_FROM_FILTERS = 'NO_RESULTS_FROM_FILTERS',
  // used by all tables if there are no results and search is applied
  NO_RESULTS_FROM_SEARCH = 'NO_RESULTS_FROM_SEARCH',
  // prescriptions table
  NO_PRESCRIPTIONS_EVER = 'NO_PRESCRIPTIONS_EVER',
  NO_PRESCRIPTIONS_OPEN = 'NO_PRESCRIPTIONS_OPEN',
  NO_PRESCRIPTIONS_AWAITING_OFFICE = 'NO_PRESCRIPTIONS_AWAITING_OFFICE',
  NO_PRESCRIPTIONS_AWAITING_PATIENT = 'NO_PRESCRIPTIONS_AWAITING_PATIENT',
  NO_PRESCRIPTIONS_COMPLETED = 'NO_PRESCRIPTIONS_COMPLETED',
  // tasks table
  NO_TASKS_OPEN = 'NO_TASKS_OPEN',
  NO_TASKS_SENT = 'NO_TASKS_SENT',
  NO_TASKS_COMPLETED = 'NO_TASKS_COMPLETED',
  // prior auth reviews table
  NO_PRIOR_AUTH_REVIEWS_OPEN = 'NO_PRIOR_AUTH_REVIEWS_OPEN',
  NO_PRIOR_AUTH_REVIEWS_REVIEWED = 'NO_PRIOR_AUTH_REVIEWS_REVIEWED',
  // notifications table
  NO_NOTIFICATIONS_OPEN = 'NO_NOTIFICATIONS_OPEN',
  NO_NOTIFICATIONS_COMPLETED = 'NO_NOTIFICATIONS_COMPLETED',
  // expiring prior auths table
  NO_EXPIRING_PRIOR_AUTHS_OPEN = 'NO_EXPIRING_PRIOR_AUTHS_OPEN',
  NO_EXPIRING_PRIOR_AUTHS_DISMISSED = 'NO_EXPIRING_PRIOR_AUTHS_DISMISSED',
  // offices
  NO_OFFICES = 'NO_OFFICES',
  // providers table
  NO_PROVIDERS = 'NO_PROVIDERS',
}

type NullStateCopyBase = {
  title?: string;
  /** Label for an action button shown below the description */
  description: React.ReactNode;
  /** Label for an action link shown below the description */
  actionLabel?: string;
  /** Variant for the action button. Defaults to 'link'. */
  actionVariant?: 'link' | 'primary';
};

type NullStateCopy =
  | (NullStateCopyBase & { icon?: null; image?: never })
  | (NullStateCopyBase & { image: string; icon?: never })
  | (NullStateCopyBase & {
      icon: { element: React.ReactNode; iconColor: string; bgColor: string };
      image?: never;
    });

// TODO(WIT-4362): fix
// eslint-disable-next-line react-refresh/only-export-components
export const nullStateCopy: Record<TableNullStateType, NullStateCopy> = {
  [TableNullStateType.NO_RESULTS_FROM_FILTERS]: {
    title: 'No results found',
    description: 'Please check the tabs and filters and try again.',
  },
  [TableNullStateType.NO_RESULTS_FROM_SEARCH]: {
    title: 'No results found',
    description: 'Please check your search term and try again.',
  },
  [TableNullStateType.NO_PRESCRIPTIONS_EVER]: {
    description:
      'Prescriptions typically appear within a few minutes after being sent, including any that were queued following verification.',
    image: prescriptionEmptyState,
    actionLabel: 'View send prescription instructions',
  },
  [TableNullStateType.NO_PRESCRIPTIONS_OPEN]: {
    title: 'No prescriptions currently in progress',
    description: 'Once a provider e-scribes a prescription to Tandem Access Support pharmacy, it will be found here.',
    image: prescriptionEmptyState,
  },
  [TableNullStateType.NO_PRESCRIPTIONS_AWAITING_OFFICE]: {
    title: "You're all caught up!",
    description:
      'Prescriptions awaiting office input will be found here. You can see open and completed prescriptions in other tabs.',
    image: prescriptionEmptyState,
  },
  [TableNullStateType.NO_PRESCRIPTIONS_AWAITING_PATIENT]: {
    title: 'No prescriptions awaiting patient input',
    description:
      'Prescriptions awaiting patient input will be found here. You can see open and completed prescriptions in other tabs.',
    image: prescriptionEmptyState,
  },
  [TableNullStateType.NO_PRESCRIPTIONS_COMPLETED]: {
    title: 'No completed prescriptions found',
    description:
      'Completed prescriptions that have been sent to the pharmacy will be found here. You can see open prescriptions in other tabs.',
    image: prescriptionEmptyState,
  },
  [TableNullStateType.NO_TASKS_OPEN]: {
    description:
      "Tasks will appear when Tandem needs input from your team. Set notification preferences to stay updated on what's relevant to you.",
    image: tasksEmptyState,
    actionLabel: 'Set notification preferences',
  },
  [TableNullStateType.NO_TASKS_SENT]: {
    title: 'No tasks awaiting Tandem found',
    description:
      'Tasks awaiting response from Tandem will be found here. You can see tasks awaiting office and closed tasks in other tabs.',
    image: tasksEmptyState,
  },
  [TableNullStateType.NO_TASKS_COMPLETED]: {
    title: 'No closed tasks found',
    description: 'Tasks that are closed will be found here. You can see open tasks in other tabs.',
    image: tasksEmptyState,
  },
  [TableNullStateType.NO_PRIOR_AUTH_REVIEWS_OPEN]: {
    description:
      "Prior authorizations that require review from your team before submission will appear here. Set notification preferences to stay updated on what's relevant to you.",
    image: paReviewsEmptyState,
    actionLabel: 'Set notification preferences',
  },
  [TableNullStateType.NO_PRIOR_AUTH_REVIEWS_REVIEWED]: {
    title: 'No reviewed PAs found',
    description: 'PAs that your team has reviewed will be found here.',
    image: paReviewsEmptyState,
  },
  [TableNullStateType.NO_NOTIFICATIONS_OPEN]: {
    description:
      "Notifications let you know of updates that don't require action from your team. Set preferences to stay updated on what's relevant to you.",
    image: notificationsEmptyState,
    actionLabel: 'Set notification preferences',
  },
  [TableNullStateType.NO_NOTIFICATIONS_COMPLETED]: {
    title: 'No completed notifications found',
    description: 'Notifications marked as completed will be found here.',
    image: notificationsEmptyState,
  },
  [TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_OPEN]: {
    description:
      "Prior authorizations that expire within the next 30 days will appear here. Set notification preferences to stay updated on what's relevant to you.",
    image: expiringPAsEmptyState,
    actionLabel: 'Set notification preferences',
  },
  [TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_DISMISSED]: {
    title: 'No dismissed expiring PAs found',
    description: 'PAs that expire in the next 30 days that your team has dismissed will be found here.',
    image: expiringPAsEmptyState,
  },
  [TableNullStateType.NO_OFFICES]: {
    title: 'No offices have been added',
    description: 'Manage related addresses and formalize tax numbers.',
    image: gradientOfficeIcon,
    actionLabel: 'Add an office',
    actionVariant: 'primary',
  },
  [TableNullStateType.NO_PROVIDERS]: {
    title: 'No providers have been added',
    description: (
      <>
        Users will be able to customize their default{' '}
        <Button
          variant="link"
          href="/settings?tab=notifications"
          textStyledAs="bodyLargeDMSans"
          style={{
            textDecorationSkipInk: 'none' as const,
            textUnderlinePosition: 'from-font' as const,
          }}
        >
          Notifications & Filters
        </Button>{' '}
        based on the NPIs added here.
      </>
    ),
    image: gradientProvider,
    actionLabel: 'Add a provider',
    actionVariant: 'primary',
  },
};

const StyledTableNullState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const StyledTableNullStateIcon = styled.div<{ $bgColor: string; $iconColor: string }>`
  display: flex;
  width: 48px;
  height: 48px;
  justify-content: center;
  align-items: center;
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 50%;

  svg {
    width: 23px;
    height: 23px;
    color: ${({ $iconColor }) => $iconColor};
  }
`;

const StyledTableNullStateText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 4px;
  width: 360px;
  color: var(--grayscale-900);
`;

export type TableNullStateProps = {
  nullStateType: TableNullStateType;
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  /** Callback for the action link (e.g., "View send prescription instructions") */
  onAction?: () => void;
  /** Name of the table for analytics tracking (e.g., 'Rx Table', 'Tasks Table') */
  tableName?: string;
};

/**
 * A component that displays a null state for a table.
 */
export const TableNullState = ({
  nullStateType,
  onClearFilters,
  onClearSearch,
  onAction,
  tableName,
}: TableNullStateProps) => {
  const [heightReady, setHeightReady] = useState(false);
  const containerId = useId();
  const copy = nullStateCopy[nullStateType];

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (container) {
      const top = container.getBoundingClientRect().top;
      // Only fill viewport when this component is in view; otherwise we'd set a negative height
      // when multiple null states are stacked (e.g. in Storybook) and cause overlapping layout
      if (top < window.innerHeight) {
        container.style.height = `calc(100vh - ${top}px)`;
      }
    }
    // We need to wait for the height to be ready before rendering the component to avoid a flash of
    // the content at the top of the page before it moves to the vertical center of the page
    setHeightReady(true);
  }, [containerId]);

  return (
    <StyledTableNullState id={containerId}>
      {heightReady && (
        <>
          {copy.image ? (
            <div className="flex w-[360px] flex-col items-center justify-center gap-4 pt-5">
              <img src={copy.image} alt="" className="aspect-[80/111] h-[111px] w-[80px] shrink-0 object-cover" />
              <div className="flex flex-col gap-1 text-center">
                {copy.title && (
                  <Typography styledAs="bodyLargeDMSans" weight="600">
                    {copy.title}
                  </Typography>
                )}
                <Typography styledAs="bodyLargeDMSans" color="var(--grayscale-900)" style={{ textAlign: 'center' }}>
                  {copy.description}
                </Typography>
              </div>
            </div>
          ) : (
            <>
              {copy.icon && (
                <StyledTableNullStateIcon $bgColor={copy.icon.bgColor} $iconColor={copy.icon.iconColor}>
                  {copy.icon.element}
                </StyledTableNullStateIcon>
              )}
              <StyledTableNullStateText>
                {copy.title && (
                  <Typography styledAs="bodyDMSans" weight="600">
                    {copy.title}
                  </Typography>
                )}
                <Typography styledAs="bodyDMSans">{copy.description}</Typography>
              </StyledTableNullStateText>
            </>
          )}
          {nullStateType === TableNullStateType.NO_RESULTS_FROM_SEARCH && onClearSearch && (
            <Button
              onClick={onClearSearch}
              variant="secondary"
              size="sm"
              {...createTrackingProps({ elementName: `${tableName ?? 'Table'}: Clear Search Button`, unmask: true })}
            >
              Clear search
            </Button>
          )}
          {nullStateType === TableNullStateType.NO_RESULTS_FROM_FILTERS && onClearFilters && (
            <Button
              onClick={onClearFilters}
              variant="secondary"
              size="sm"
              {...createTrackingProps({ elementName: `${tableName ?? 'Table'}: Clear Filters Button`, unmask: true })}
            >
              Clear filters
            </Button>
          )}
          {copy.actionLabel &&
            onAction &&
            (copy.actionVariant === 'primary' ? (
              <Button onClick={onAction}>{copy.actionLabel}</Button>
            ) : (
              <Button
                variant="link"
                textStyledAs="bodyLargeDMSans"
                onClick={onAction}
                style={{
                  color: 'var(--purple-600)',
                  fontWeight: 700,
                  textDecorationSkipInk: 'none',
                  textUnderlinePosition: 'from-font',
                }}
              >
                {copy.actionLabel}
              </Button>
            ))}
        </>
      )}
    </StyledTableNullState>
  );
};

export default TableNullState;
